// src/features/audittrail/RetentionTab.tsx
// Decide how long each record type is kept, and purge what has aged out.
// ListRetentionPolicies, CreateRetentionPolicy, GetRetentionPolicy,
// UpdateRetentionPolicy, ExecuteRetentionPurge.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Badge, Alert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { RetentionPolicy } from '@core/types/audittrail';
import { RetentionFormDialog } from './RetentionFormDialog';
import { enumKey, errMessage } from './logic';

export const RetentionTab = observer(() => {
  const { t } = useI18n();
  const [items, setItems] = useState<RetentionPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RetentionPolicy | null>(null);
  const [purging, setPurging] = useState<RetentionPolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    audittrailStore.ListRetentionPolicies()
      .then((res) => setItems(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('audittrail.retention.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (row: RetentionPolicy) => {
    if (row.entity?.id) {
      try {
        const res = await audittrailStore.GetRetentionPolicy(row.entity.id);
        setEditing(res.policy ?? row);
      } catch {
        setEditing(row);
      }
    } else {
      setEditing(row);
    }
    setFormOpen(true);
  };

  const purge = async () => {
    if (!purging?.entity?.id) return;
    setBusy(true);
    try {
      const res = await audittrailStore.ExecuteRetentionPurge(purging.entity.id, { policy: purging.entity });
      setPurgeResult(t('audittrail.retention.purgeResult', { purged: res.records_purged, anonymized: res.records_anonymized, archived: res.records_archived }));
      setPurging(null);
      load();
    } catch {
      setError(t('audittrail.retention.purgeError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('audittrail.retention.subtitle')}</p>
        <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>{t('audittrail.retention.add')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {purgeResult && <Alert variant="success" dismissible onDismiss={() => setPurgeResult(null)}>{purgeResult}</Alert>}

      <Table hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('audittrail.retention.column.pattern')}</TableHead>
            <TableHead>{t('audittrail.retention.column.days')}</TableHead>
            <TableHead>{t('audittrail.retention.column.framework')}</TableHead>
            <TableHead>{t('audittrail.retention.column.strategy')}</TableHead>
            <TableHead>{t('audittrail.retention.column.active')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableEmpty colSpan={6} message={t('audittrail.retention.empty')} />
          ) : (
            items.map((p) => (
              <TableRow key={p.entity?.id}>
                <TableCell className="font-mono text-xs">{p.entity_type_pattern}</TableCell>
                <TableCell className="text-sm">{p.retention_days}</TableCell>
                <TableCell className="text-sm">{t(`audittrail.complianceFramework.${enumKey(p.compliance_framework, 'COMPLIANCE_FRAMEWORK_')}`)}</TableCell>
                <TableCell className="text-sm">{t(`audittrail.purgeStrategy.${enumKey(p.purge_strategy, 'PURGE_STRATEGY_')}`)}</TableCell>
                <TableCell><Badge variant={p.is_active ? 'success' : 'secondary'} size="sm">{p.is_active ? t('common.enabled') : t('common.disabled')}</Badge></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>{t('common.edit')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setPurging(p)}>{t('audittrail.retention.purgeNow')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <RetentionFormDialog open={formOpen} policy={editing} onClose={() => setFormOpen(false)} onSaved={load} />
      <ConfirmDialog
        open={!!purging}
        title={t('audittrail.retention.purgeNow')}
        description={t('audittrail.retention.purgeConfirm')}
        destructive
        isConfirming={busy}
        onConfirm={purge}
        onCancel={() => setPurging(null)}
      />
    </div>
  );
});

export default RetentionTab;
